import { useEffect, useRef, useState } from "react";

import { useModalHistory } from "../../lib/modalHistory";

/**
 * Potwierdzenie „opuść stronę” przy wstecz, gdy jest niezapisany formularz
 * poza modalem (parity z MPA backNavigationModal). Nie obejmuje otwartych
 * overlay — te zamyka ModalHistory.
 */
export function BackNavigationGuard() {
  const historyApi = useModalHistory();
  const [open, setOpen] = useState(false);
  const allowOnce = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    function onInput(event: Event) {
      const target = event.target as HTMLElement | null;
      const form = target?.closest?.("form");
      if (!form || form.closest(".modal-overlay")) {
        return;
      }
      form.dataset.modified = "true";
      dirty.current = true;
    }

    function onSubmit(event: Event) {
      const form = event.target as HTMLFormElement | null;
      if (!form) {
        return;
      }
      window.setTimeout(() => {
        form.dataset.modified = "false";
        dirty.current = false;
      }, 100);
    }

    document.addEventListener("input", onInput, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  useEffect(() => {
    function inProcess(): boolean {
      if (historyApi?.hasOpen()) {
        return false;
      }
      if (document.querySelector('[data-testid="tags-page"] [data-testid="tag-input"]')) {
        return true;
      }
      const forms = document.querySelectorAll("form");
      for (const form of forms) {
        if (form.dataset.modified === "true" && !form.closest(".modal-overlay")) {
          return true;
        }
      }
      return dirty.current;
    }

    function onPopState() {
      if (allowOnce.current) {
        allowOnce.current = false;
        return;
      }
      if (historyApi?.isConsumingPopstate()) {
        return;
      }
      if (historyApi?.hasOpen()) {
        return;
      }
      if (!inProcess()) {
        return;
      }
      setOpen(true);
      history.pushState({ appPage: true }, "", location.href);
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [historyApi]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="back-nav-modal-title"
      data-testid="back-navigation-modal"
      // Wykluczone z ModalHistory — własny przepływ historii.
      id="backNavigationModal"
    >
      <div className="modal">
        <h3 className="modal__title" id="back-nav-modal-title">
          Niezapisane zmiany
        </h3>
        <p className="modal__text">Masz niezapisane zmiany lub jesteś w trakcie procesu.</p>
        <p className="modal__text modal__text--sm">
          Czy na pewno chcesz opuścić tę stronę? Niezapisane dane zostaną utracone.
        </p>
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="back-nav-stay"
            onClick={() => {
              setOpen(false);
              history.pushState({ appPage: true }, "", location.href);
            }}
          >
            Zostań tutaj
          </button>
          <button
            type="button"
            className="btn btn--danger"
            data-testid="back-nav-leave"
            onClick={() => {
              dirty.current = false;
              document.querySelectorAll("form").forEach((form) => {
                form.dataset.modified = "false";
              });
              setOpen(false);
              allowOnce.current = true;
              history.back();
            }}
          >
            Opuść stronę
          </button>
        </div>
      </div>
    </div>
  );
}
